import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

/**
 * Calculate approximate sunset time for today
 * Uses a simple approximation: sunset is around 6-7 PM depending on season
 * For more accuracy, you could use a geolocation API or a library like suncalc
 */
function getSunsetTime() {
  const now = new Date();
  const hours = now.getHours();
  const month = now.getMonth(); // 0-11
  
  // Approximate sunset times by month (in 24-hour format)
  // These are rough estimates - adjust based on your target location
  const sunsetHours = {
    0: 17,  // January: 5 PM
    1: 18,  // February: 6 PM
    2: 19,  // March: 7 PM
    3: 19,  // April: 7 PM
    4: 20,  // May: 8 PM
    5: 20,  // June: 8 PM
    6: 20,  // July: 8 PM
    7: 19,  // August: 7 PM
    8: 18,  // September: 6 PM
    9: 17,  // October: 5 PM
    10: 17, // November: 5 PM
    11: 17, // December: 5 PM
  };
  
  const sunsetHour = sunsetHours[month] || 18;
  const sunset = new Date(now);
  sunset.setHours(sunsetHour, 0, 0, 0);
  
  return sunset;
}

/**
 * Check if it's after sunset
 */
function isAfterSunset() {
  const now = new Date();
  const sunset = getSunsetTime();
  return now >= sunset;
}

export function ThemeProvider({ children }) {
  // Check if user has manually set a preference
  const [manualOverride, setManualOverride] = useState(() => {
    const saved = localStorage.getItem('theme-preference');
    return saved === 'dark' || saved === 'light' ? saved : null;
  });

  const [isDark, setIsDark] = useState(() => {
    // If user has manually set preference, use that
    if (manualOverride === 'dark') return true;
    if (manualOverride === 'light') return false;
    // Otherwise, use sunset detection
    return isAfterSunset();
  });

  // Update theme when manual override changes
  useEffect(() => {
    if (manualOverride === 'dark') {
      setIsDark(true);
    } else if (manualOverride === 'light') {
      setIsDark(false);
    } else {
      // Auto mode - use sunset detection
      setIsDark(isAfterSunset());
    }
  }, [manualOverride]);

  useEffect(() => {
    // Only auto-update if not in manual mode
    if (manualOverride !== null) return;

    // Check theme every minute
    const interval = setInterval(() => {
      const shouldBeDark = isAfterSunset();
      setIsDark(shouldBeDark);
    }, 60000); // Check every minute

    // Also check immediately
    setIsDark(isAfterSunset());

    return () => clearInterval(interval);
  }, [manualOverride]);

  // Apply theme class to document
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [isDark]);

  const toggleTheme = () => {
    if (manualOverride === 'dark') {
      setManualOverride('light');
      localStorage.setItem('theme-preference', 'light');
    } else if (manualOverride === 'light') {
      setManualOverride(null); // Switch to auto mode
      localStorage.removeItem('theme-preference');
      setIsDark(isAfterSunset()); // Apply sunset detection immediately
    } else {
      // Currently in auto mode, switch to opposite of current
      const newMode = isDark ? 'light' : 'dark';
      setManualOverride(newMode);
      localStorage.setItem('theme-preference', newMode);
    }
  };

  return (
    <ThemeContext.Provider value={{ isDark, setIsDark, toggleTheme, manualOverride }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

